import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { debounceTime, filter, finalize } from 'rxjs/operators';
import { AnnotationAttachmentManagerComponent } from 'src/app/components/annotation-view/annotation-attachment-manager/annotation-attachment-manager.component';
import { DateTimePickerComponent, IDateTimePickerResultType } from 'src/app/components/date-time-picker/date-time-picker.component';
import { LoadingOverlayComponent } from 'src/app/components/loading-overlay/loading-overlay.component';
import { AutoFocusDirective } from 'src/app/directives/auto-focus/auto-focus.directive';
import { ButtonClickDirective } from 'src/app/directives/buttons/button-click-action/button-click.directive';
import { IonicImports, MaterialImportsModule } from 'src/app/material-imports.module';
import { IAnnotation } from 'src/app/model/thingie/annotation';
import { IThingie } from 'src/app/model/thingie/thingie';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FlatpickrTooltipService } from 'src/app/services/flatpickr-tooltip/flatpickr-tooltip.service';
import { AnnotationService } from 'src/app/services/thingie/annotation-service.service';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { ConfirmationResults, UINotificationService } from 'src/app/services/ui-notification/uinotification.service';
import { deepCopySync } from 'src/app/utility/deep-copy';
import { IDateClassTuple } from 'src/app/utility/flatpickr/plugins/add-class-to-date-plugin';
import { IFlatpickrDayHoverEvent } from 'src/app/utility/flatpickr/plugins/emit-targeted-hover-events-plugin';
import { defaultThingieComparator } from 'src/app/utility/thingie/default-thingie-comparator';
import { isNotUndefined } from 'src/app/utility/types';
import { DefaultConfig } from 'src/assets/default.config';
import { SubSink } from 'subsink';
import { SpinnerComponent } from '../../../spinner/spinner.component';
import { DialogControl } from '../../dialog/dialog-control';
import { DialogTitleRowExtraContentDirective } from '../../dialog/dialog-title-row-extra-content.directive';
import { IIsDialog } from '../../dialog/dialog.service';

/**
 * Mode in which to open {@link AnnotationHandlingDialogComponent}
 */
export enum AnnotationHandlingDialogMode {

  /**
   * Create new annotation (in potentially
   * multiple related thingies)
   */
  Create,

  /**
   * Edit existing annotation of one thingie
   */
  Edit,

  /**
   * Duplicate annotation (to potentially
   * multiple related thingies)
   */
  Duplicate
}


/**
 * Arguments passed to {@link AnnotationHandlingDialogComponent}
 * when opened in `add`/`new annotation`-mode
 */
export interface AddModeArgs {
  mode: AnnotationHandlingDialogMode.Create;

  /**
   * The thingie to which the annotation
   * will be added by default.
   *
   * If null, only siblingThingies will be used.
   */
  defaultThingie: IThingie | null;

  /**
   * Siblings/replicates of `currentObject`
   * to which the annotation can optionally
   * be added to, too.
   */
  siblingThingies: IThingie[];

  /**
   * The ID of the project that the thingie of the annotation belongs to.
   */
  projectId: string;
}


/**
 * Arguments passed to {@link AnnotationHandlingDialogComponent}
 * when opened in `duplication`-mode
 */
export interface DuplicateModeArgs extends Omit<AddModeArgs, 'mode'> {
  mode: AnnotationHandlingDialogMode.Duplicate;

  annotationIndex: number;

  annotation: IAnnotation;

  /**
   * The ID of the project that the thingie of the annotation belongs to.
   */
  projectId: string;
}


/**
 * Arguments passed to {@link AnnotationHandlingDialogComponent}
 * when opened in `edit`-mode
 */
export interface EditModeArgs {
  mode: AnnotationHandlingDialogMode.Edit;

  /**
   * ID of thingie for which to edit the annotation
   */
  thingieId: string;

  /**
   * Index of annotation in thingie's `process 0`
   */
  annotationIndex: number;

  /**
   * The annotation itself
   */
  annotation: IAnnotation;

  /**
   * The ID of the project that the thingie of the annotation belongs to.
   */
  projectId: string;
}


/**
 * Type of arguments passed to and required by
 * {@link AnnotationHandlingDialogComponent}
 */
export type AnnotationHandlingDialogArgs =
  AddModeArgs
  | DuplicateModeArgs
  | EditModeArgs;


/**
 * Dialog for handling annotations
 *
 * Actions available in the dialog
 * - Add new annotation
 * - Edit existing annotation
 * - Duplicate (copy) existing annotation
 * - Delete existing annotation
 */
@Component({
  templateUrl: './annotation-handling-dialog.component.html',
  styleUrls: ['./annotation-handling-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ MaterialImportsModule,
    ReactiveFormsModule,
    CommonModule,
    AnnotationAttachmentManagerComponent,
    IonicImports,
    TextFieldModule,
    LoadingOverlayComponent,
    SpinnerComponent,
    DialogTitleRowExtraContentDirective,
    DateTimePickerComponent,
    MaterialImportsModule,
    AutoFocusDirective,
    ButtonClickDirective,
  ],
  providers: [FlatpickrTooltipService]
})
export class AnnotationHandlingDialogComponent implements AfterViewInit, OnDestroy, IIsDialog<AnnotationHandlingDialogArgs, undefined> {
  public readonly __compileTimeTypeInferenceHelper: [AnnotationHandlingDialogArgs, undefined][] = [];

  @ViewChild(AnnotationAttachmentManagerComponent, { static: true })
  private _attachmentManager!: AnnotationAttachmentManagerComponent;

  @ViewChild('dialogButtons', { read: TemplateRef<unknown> })
  private set dialogButtons(ref: TemplateRef<unknown>) {
    this.dialogControl.setActionButtonsTemplate(ref);
  }

  /**
   * Forward enum to view template
   *
   * @private
   */
  protected readonly ModeType = AnnotationHandlingDialogMode;


  /**
   * The mode the dialog is in
   *
   * @private
   */
  protected mode: AnnotationHandlingDialogMode;


  /**
   * @private
   */
  protected dateControl = new FormControl<IDateTimePickerResultType>(null);


  /**
   * @private
   */
  protected annotation: IAnnotation;

  /**
   * Whether the annotation was updated (attachments added/removed) by user
   */
  protected annotationAttachmentUpdated = signal<boolean>(false);

  /**
   * Thingies to which the annotation will be added.
   *
   * Will always contain the default thingie at index 0.
   *
   * @private
   */
  protected selectedThingies: (IThingie | 'all')[];


  /**
   * Source list to generate the mat-options from.
   *
   * Will contain the default thingie at index 0.
   *
   * @private
   */
  protected readonly possibleThingies: IThingie[];


  /**
   * The number of replicates selected,
   * excluding the default thingie and the `all`-option.
   *
   * @private
   */
  protected numberOfReplicatesSelected = 0;


  /**
   * Annotation's comment
   *
   * @private
   */
  protected readonly commentFormControl = new FormControl('', [Validators.required]);


  /**
   * Whether the dialog is currently in the process of doing something
   *
   * @private
   */
  protected isWorking = false;


  /**
   * @private
   */
  protected readonly _defaultThingie?: IThingie;


  /**
   * Used to determine the state change with regards to `all`
   *
   * @see {@link _replicateSelectionChange}
   */
  private recentSelection: (IThingie | 'all')[] = [];


  private readonly subsink = new SubSink();

  private isEditor = signal<boolean>(false);

  /**
   * Set by {@link initializeTitle}
   */
  public title$!: Observable<string>;

  /**
   * Used to apply styling to the start and end date of the experiment within
   * the date time picker.
   */
  protected readonly dateTimePickerDateClasses = signal<IDateClassTuple[]>([]);


  private dialogArgs: AnnotationHandlingDialogArgs;

  constructor(
    private annotationService: AnnotationService,
    private changeDetector: ChangeDetectorRef,
    private dialogControl: DialogControl<AnnotationHandlingDialogArgs>,
    thingieService: ThingieService,
    private uiNotifications: UINotificationService,
    private auth: AuthService,
    private flatpickrTooltipService: FlatpickrTooltipService,
  ) {
    this.dialogArgs = dialogControl.data;

    this.initializeTitle();

    this.mode = this.dialogArgs.mode;
    this.annotation = this.prepareAnnotation(this.dialogArgs);
    this.dateControl.setValue(DateTime.fromISO(this.annotation.timestamp).toJSDate());

    if (
      this.dialogArgs.mode === AnnotationHandlingDialogMode.Create
      || this.dialogArgs.mode === AnnotationHandlingDialogMode.Duplicate
    ) {
      this._defaultThingie = this.dialogArgs.defaultThingie ?? undefined;
    }

    this.possibleThingies = this.prepareTargetThingies(this.dialogArgs);
    if (this._defaultThingie) {
      this.selectedThingies = [this._defaultThingie!];
    } else {
      this.selectedThingies = [];
    }

    this.subsink.sink = this.commentFormControl.valueChanges.pipe(
      debounceTime(DefaultConfig.general.userInputDefaultDebounce_Milliseconds),
      finalize(() => this.changeDetector.markForCheck())
    ).subscribe(comment => this.annotation.label = comment!);

    void this.auth.hasEditorRoleInProject(this.dialogArgs.projectId)
      .then(isEditor => {
        this.isEditor.set(isEditor);
        this.changeDetector.markForCheck();
      });

    this.subsink.sink = this.dateControl.valueChanges
      .pipe(filter(v => v !== null && this.dateControl.valid))
      .subscribe(v => this.annotation.timestamp = v!.toISOString());

    this.enableExperimentDatesHighlightingInDateTimePicker(thingieService);
  }

  private enableExperimentDatesHighlightingInDateTimePicker(thingieService: ThingieService) {
    let refThingieId: string | undefined;
    if (this.dialogArgs.mode === AnnotationHandlingDialogMode.Edit) {
      refThingieId = this.dialogArgs.thingieId;
    } else if (
      this.dialogArgs.mode === AnnotationHandlingDialogMode.Create
      || this.dialogArgs.mode === AnnotationHandlingDialogMode.Duplicate
    ) {
      refThingieId = this.dialogArgs.defaultThingie?._id ?? this.dialogArgs.siblingThingies[0]?._id;
    }

    if (!refThingieId) {
      return;
    }

    this.subsink.sink = thingieService
      .getExperimentDates(refThingieId)
      .subscribe(dates => {
        const classes: IDateClassTuple[] = [];
        if (!dates.startDate) {
          return;
        }

        classes.push({
          class: 'flatpickr-daymatch-start',
          date: new Date(dates.startDate)
        });

        if (dates.stopDate) {
          classes.push({
            class: 'flatpickr-daymatch-end',
            date: new Date(dates.stopDate)
          });
        }
        this.dateTimePickerDateClasses.set(classes);
      });
  }

  private initializeTitle() {
    switch (this.dialogArgs.mode) {
      case AnnotationHandlingDialogMode.Create:
        this.title$ = new Observable(o => {
          o.next('Add new annotation'); o.complete();
        });
        break;
      case AnnotationHandlingDialogMode.Edit:
        this.title$ = new Observable(o => {
          o.next('Edit annotation'); o.complete();
        });
        break;
      case AnnotationHandlingDialogMode.Duplicate:
        this.title$ = new Observable(o => {
          o.next('Duplicate annotation'); o.complete();
        });
        break;
      default:
        this.title$ = new Observable(o => {
          o.next('Annotation'); o.complete();
        });
    }
  }

  public ngOnDestroy(): void {
    this.flatpickrTooltipService.dispose();
    this.subsink.unsubscribe();
  }

  public ngAfterViewInit(): void {
    if (this.dialogArgs.mode !== AnnotationHandlingDialogMode.Create) {
      this.commentFormControl.setValue(this.annotation.label);
    } else if (
      this.dialogArgs.mode === AnnotationHandlingDialogMode.Create
      && !this._defaultThingie
    ) {
      this._replicateSelectionChange({ value: ['all'] });
    }

    this.changeDetector.detectChanges();
  }


  /**
   * Prepares the annotation depending on the dialog data
   * (in particular the mode)
   *
   * @param dialogArgs
   * @returns
   */
  private prepareAnnotation(dialogArgs: AnnotationHandlingDialogArgs): IAnnotation {
    switch (dialogArgs.mode) {
      case AnnotationHandlingDialogMode.Create:
        return {
          creator: '',
          deleted: false,
          hidden: false,
          files: [],
          label: '',
          timestamp: DateTime.now().toISO(),
          systemAnnotation: false
        };
      case AnnotationHandlingDialogMode.Duplicate: {
        const annotation = deepCopySync(dialogArgs.annotation);
        // Currently we don't copy attachments
        annotation.files = [];
        annotation.timestamp = DateTime.now().toISO();
        annotation.creator = '';
        annotation.systemAnnotation = false;
        return annotation;
      }
      case AnnotationHandlingDialogMode.Edit:
        return deepCopySync(dialogArgs.annotation);
    }
  }


  /**
   * Handle change in selection of replicates
   *
   * @private
   * @param value new selection of entries
   */
  protected _replicateSelectionChange({ value }: { readonly value: [IThingie | 'all'] }) {
    const allIdx              = value.findIndex(v => v === 'all');
    const newIncludesAll      = allIdx !== -1;
    const previousIncludesAll = this.recentSelection.includes('all');

    let newCount = value.length;
    if (newIncludesAll) {
      // remove the 'all'-option from the count
      --newCount;
    }

    const diff = this.possibleThingies.length - newCount;

    if (!newIncludesAll && previousIncludesAll) {
      // Deselect all
      this.selectedThingies = [];
    } else if ((newIncludesAll && !previousIncludesAll) || diff === 0) {
      // either the `all`-option was just selected or each
      // entry is now checked
      // => select all entries and add the `all`-option
      // (needs to be checked after `!newIncludesAll && recentIncludesAll`,
      // otherwise `diff === 0` will prevent 'deselection-of-all'.

      this.selectedThingies = ['all', ...this.possibleThingies];
    } else {  // diff > 0
      // Subset selected (remove  `all`-option if present)

      this.selectedThingies = value.filter(v => v !== 'all');
    }

    this.recentSelection = [...this.selectedThingies];

    this.updateNumberOfReplicatesSelected();
  }


  private updateNumberOfReplicatesSelected() {
    this.numberOfReplicatesSelected = this.selectedThingies.length
      // deduct 'all'-option if present
      - (this.selectedThingies.includes('all') ? 1 : 0);
  }

  /**
   * Prepares the list of thingies to which the annotation
   * can be added to.
   *
   * The list will contain the default thingie at index 0.
   *
   * @param args
   * @returns
   */
  private prepareTargetThingies(args: AnnotationHandlingDialogArgs): IThingie[] {
    switch (args.mode) {
      case AnnotationHandlingDialogMode.Create:
      case AnnotationHandlingDialogMode.Duplicate:
      {
        let result: IThingie[];
        if (args.defaultThingie === null) {
          result = args.siblingThingies;
        } else {
          result = [args.defaultThingie!, ...args.siblingThingies];
        }


        return result.filter(isNotUndefined).sort(defaultThingieComparator);
      }

      case AnnotationHandlingDialogMode.Edit:
      default:
        return [];
    }
  }


  /**
   * Save-button handler
   *
   * @private
   */
  protected async _save() {
    try {
      this.isWorking = true;
      this.changeDetector.markForCheck();
      switch (this.dialogArgs.mode) {
        case AnnotationHandlingDialogMode.Create:
        case AnnotationHandlingDialogMode.Duplicate:
          await this._saveNew();
          break;
        case AnnotationHandlingDialogMode.Edit:
          await this._saveEdit();
      }
    } finally {
      this.isWorking = false;
      this.changeDetector.markForCheck();
    }

    const message = this.dialogArgs.mode === AnnotationHandlingDialogMode.Edit
      ? 'Annotation changes saved'
      : 'Annotation created';

    this.uiNotifications.displaySnackbar({
      message
    });
  }

  /**
   * Creates a new annotation based on the values
   * configured in the dialog
   */
  protected async _saveNew() {
    const annotation = this.annotation;
    const thingies = this.selectedThingies.filter(t => t !== 'all')
      .map(t => (t as IThingie)._id!);

    const { locations: annotationLocations } = await this.annotationService.createAnnotations({
      thingies,
      annotations: [annotation]
    });

    // Add attachments
    await this._attachmentManager.commitChanges(annotationLocations);

    await this.dialogControl.requestCloseDialog(undefined);
  }

  /**
   * Saves the edits made to the annotation in `edit`-mode
   */
  protected async _saveEdit() {
    const dialogArgs = this.dialogArgs as EditModeArgs;

    const thingieId       = dialogArgs.thingieId;
    const annotationIndex = dialogArgs.annotationIndex;
    const annotation      = this.annotation;

    await this.annotationService.editAnnotation(
      thingieId,
      annotationIndex,
      annotation
    );

    // As currently we don't return the updated annotation from
    // `commitChanges`, we need to run this after saving the edits
    // not to overwrite anything by calling `editAnnotation`
    await this._attachmentManager.commitChanges({
      annotationIndex,
      processIndex: 0,
      thingieId
    });

    await this.dialogControl.requestCloseDialog(undefined);
  }


  /**
   * @private
   */
  protected async _deleteAnnotation() {
    if (!this.isEditor()) {
      return;
    }

    const args = this.dialogArgs as EditModeArgs;

    try {
      const choice = await this.uiNotifications.askForConfirmation({
        message: 'Please confirm the deletion of the annotation:',
        approvalButton: {
          text: 'Delete annotation',
          color: 'warn'
        },
        rejectionButton: {
          text: 'Keep annotation'
        },
        title: 'Delete annotation'
      });


      if (choice === ConfirmationResults.No) {
        return;
      }


      await this.annotationService.deleteAnnotations(
        args.thingieId,
        args.annotationIndex
      );

      await this.dialogControl.requestCloseDialog(undefined);

      this.uiNotifications.displaySnackbar({
        message: 'Annotation deleted',
      });
    } catch {
      this.uiNotifications.displayErrorAlert(
        {
          message: 'Could not delete annotation',
          title: 'Deletion failed',
        },
        null
      );
    }
  }

  protected closeDialog() {
    void this.dialogControl.requestCloseDialog(undefined);
  }

  protected dateTimePickerHoverIn($event: IFlatpickrDayHoverEvent) {
    this.flatpickrTooltipService.showTooltip($event);
  }

  protected dateTimePickerHoverOut(_: IFlatpickrDayHoverEvent) {
    this.flatpickrTooltipService.hideTooltip();
  }

  protected _annotationAttachmentUpdated() {
    this.annotationAttachmentUpdated.set(true);
  }
}
